import React, { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { authClient } from "../lib/auth";

/**
 * Authentication context type definition
 *
 * Provides session state and authentication methods throughout the application.
 */
export interface AuthContextType {
	user: any | null;
	session: any | null;
	loading: boolean;
	signIn: (email: string, password: string) => Promise<{ error: string | null }>;
	signOut: () => Promise<void>;
	refresh: () => Promise<void>;
}

/**
 * Default context values for when used outside provider
 */
const AuthContextDefaultValues: AuthContextType = {
	user: null,
	session: null,
	loading: true,
	signIn: async () => ({ error: "Not initialized" }),
	signOut: async () => {},
	refresh: async () => {},
};

/**
 * Authentication Context
 *
 * Manages user session state and provides auth methods to components.
 * Wraps the application to provide auth context to all child components.
 */
export const AuthContext = createContext<AuthContextType>(AuthContextDefaultValues);

/**
 * AuthProvider Component
 *
 * Provides authentication context to child components.
 * Fetches session on mount and manages auth state.
 *
 * @param children - React components to wrap with auth context
 */
export function AuthProvider({ children }: { children: ReactNode }) {
	const [user, setUser] = useState<any | null>(null);
	const [session, setSession] = useState<any | null>(null);
	const [loading, setLoading] = useState(true);

	/**
	 * Fetch current session from backend
	 */
	const fetchSession = async () => {
		try {
			const sessionData = await authClient.getSession();
			// Better-Auth returns data property with session information
			const data = sessionData.data;
			setUser(data?.user || null);
			setSession(data?.session || null);
		} catch (error) {
			console.error("Failed to fetch session:", error);
			setUser(null);
			setSession(null);
		} finally {
			setLoading(false);
		}
	};

	/**
	 * Sign in with email and password
	 *
	 * @param email - User email
	 * @param password - User password
	 * @returns Promise with error message if sign in fails
	 */
	const signIn = async (email: string, password: string): Promise<{ error: string | null }> => {
		setLoading(true);
		try {
			const result = await authClient.signIn.email({ email, password });
			if (result.error) {
				return { error: result.error.message || "Sign in failed" };
			}
			// Use data from the signIn response directly instead of a separate
			// getSession() call. Safari ITP and Samsung browser block third-party
			// cookies, so the session cookie may not be stored yet when getSession
			// fires, leaving user as null and preventing the redirect.
			if (result.data) {
				setUser(result.data.user || null);
				// The signIn response does not always include the session object
				// (Better-Auth returns the token here); fall back to null and let
				// fetchSession populate it later.
				setSession((result.data as any).session || null);
			} else {
				await fetchSession();
			}
			return { error: null };
		} catch (error) {
			console.error("Sign in failed:", error);
			return { error: "An unexpected error occurred" };
		} finally {
			setLoading(false);
		}
	};

	/**
	 * Sign out current user
	 */
	const signOut = async () => {
		setLoading(true);
		try {
			await authClient.signOut();
			setUser(null);
			setSession(null);
		} catch (error) {
			console.error("Sign out failed:", error);
			throw error;
		} finally {
			setLoading(false);
		}
	};

	/**
	 * Refresh session data
	 */
	const refresh = async () => {
		setLoading(true);
		await fetchSession();
	};

	// Fetch session on mount
	useEffect(() => {
		fetchSession();
	}, []);

	const value: AuthContextType = {
		user,
		session,
		loading,
		signIn,
		signOut,
		refresh,
	};

	return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
